import React, { useState, useEffect, useCallback } from 'react';
import { 
  GameBoard, 
  GameModeSelector, 
  CustomizationPanel, 
  StatisticsDisplay,
  ResponsiveLayout,
  ResponsiveStack,
  ResponsiveButton,
  ResponsiveModal,
  LoadingIndicator,
  ErrorBoundary,
  GameHub,
  AuthButton,
  AuthMenu
} from './index';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from './UniversalGameModeSelector';
import { UniversalGameLayout, UniversalGameLayoutConfig } from './UniversalGameLayout';
import { TopNavBar, NavMenuItem } from './TopNavBar';
import { ChessGame } from '../chess/components/ChessGame';
import { CheckersGame } from '../checkers/components/CheckersGame';
import { ConnectFourGame } from '../connect-four/components/ConnectFourGame';
import { BattleshipGame } from '../battleship/components/BattleshipGame';
import { PokerGame } from '../poker/components/PokerGame';
import { MafiaGame } from '../mafia/components/MafiaGame';
import { SnakesLaddersGame } from '../snakes-and-ladders/components/SnakesLaddersGame';
import { LudoGame } from '../ludo/components/LudoGame';
import { TeenPattiGame } from '../teen-patti/components/TeenPattiGame';
import { ReversiGame } from '../reversi/components/ReversiGame';
import { MancalaGame } from '../mancala/components/MancalaGame';
import { BackgammonGame } from '../backgammon/components/BackgammonGame';
import { UnoGame } from '../uno/components/UnoGame';
import { PachisiGame } from '../pachisi/components/PachisiGame';
import { CarromGame } from '../carrom/components/CarromGame';
import { GoGame } from '../go/components/GoGame';
import { SudokuGame } from '../sudoku/components/SudokuGame';
import { Game2048 } from '../2048/components/Game2048';
import { MinesweeperGame } from '../minesweeper/components/MinesweeperGame';
import { SolitaireGame } from '../solitaire/components/SolitaireGame';
import { HangmanGame } from '../hangman/components/HangmanGame';
import { DotsBoxesGame } from '../dots-and-boxes/components/DotsBoxesGame';
import { RummyGame } from '../rummy/components/RummyGame';
import { PinballGame } from '../pinball/components/PinballGame';
import { ChauparGame } from '../chaupar/components/ChauparGame';
import { GolfGame } from '../golf-card-game/components';
import { CourtSplitter } from '../court-splitter/components/CourtSplitter';
import { WordleGame } from '../wordle/components';
import { WordGame } from '../word-game/components';
import { YahtzeeGame } from '../yahtzee/components';
import { BilliardsGame } from '../billiards/components';
import { TriviaGame } from '../trivia/components';
import { AdminApp } from '../admin';
import { HowToPlay } from './HowToPlay';
import { allGameInstructions } from '../data/gameInstructions';
import { 
  GameEngine,
  ProfileManager,
  StatisticsManager,
  PreferencesManager,
  NotificationService,
  GameSessionManager
} from '../services';
import WebSocketMultiplayer from './WebSocketMultiplayer';
import { useErrorHandling } from '../hooks/useErrorHandling';
import { ErrorType, ErrorSeverity } from '../services/ErrorHandlingService';
import { useAuthentication } from '../hooks/useAuthentication';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationType } from '../services/NotificationService';
import { AuthMethod } from '../types';
import {
  GameSession,
  GameMode,
  AIDifficulty,
  GameState,
  Position,
  Player,
  PlayerType,
  GameStatus,
  Profile,
  UserPreferences,
  GameResult
} from '../types';
import './GameApp.css';

type AppView = 'hub' | 'menu' | 'game' | 'stats' | 'settings' | 'websocket-multiplayer' | 'chess' | 'checkers' | 'connect-four' | 'battleship' | 'poker' | 'mafia' | 'snakes-and-ladders' | 'ludo' | 'teen-patti' | 'reversi' | 'mancala' | 'backgammon' | 'uno' | 'pachisi' | 'carrom' | 'go' | 'sudoku' | '2048' | 'minesweeper' | 'solitaire' | 'hangman' | 'dots-and-boxes' | 'rummy' | 'pinball' | 'chaupar' | 'golf-card-game' | 'court-splitter' | 'wordle' | 'word-game' | 'yahtzee' | 'billiards' | 'trivia' | 'admin';

interface GameAppProps {
  onLaunchMode?: (mode: string) => void;
}

export const GameApp: React.FC<GameAppProps> = ({ onLaunchMode }) => {
  // Core state
  const [currentView, setCurrentView] = useState<AppView>('hub');
  const [currentGame, setCurrentGame] = useState<GameSession | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentGameSessionId, setCurrentGameSessionId] = useState<string | null>(null);
  const [deepLinkRoomCode, setDeepLinkRoomCode] = useState<string | null>(null);
  const [showAuthMenu, setShowAuthMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Services - use useState to ensure single instances
  const [gameEngine] = useState(() => new GameEngine());
  const [profileManager] = useState(() => new ProfileManager());
  const [statsManager] = useState(() => new StatisticsManager(profileManager));
  const [preferencesManager] = useState(() => new PreferencesManager());
  const [gameSessionManager] = useState(() => GameSessionManager.getInstance());
  
  // Hooks
  const [errorState, errorActions] = useErrorHandling('GameApp');
  const { user: profile, isAuthenticated, login, logout, createProfile, isLoading: authLoading, guestGameCount, incrementGuestGameCount } = useAuthentication();
  const { addNotification } = useNotifications();

  // Initialize app
  useEffect(() => {
    initializeApp();
  }, []);

  // Handle launch mode from PWA shortcuts
  useEffect(() => {
    if (onLaunchMode) {
      onLaunchMode('ready');
    }
  }, [onLaunchMode]);

  // Handle deep links on component mount
  useEffect(() => {
    const handleDeepLink = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const pathname = window.location.pathname;
      
      console.log('🔗 Deep link check:', {
        pathname,
        search: window.location.search,
        fullURL: window.location.href,
        roomCode: urlParams.get('roomCode')
      });
      
      // Handle /tic-tac-toe path with roomCode parameter
      if (pathname === '/tic-tac-toe' || pathname.includes('tic-tac-toe')) {
        const roomCode = urlParams.get('roomCode');
        console.log('🎯 Tic-tac-toe path detected, roomCode:', roomCode);
        
        if (roomCode) {
          console.log('🔗 Deep link detected: joining room', roomCode);
          setDeepLinkRoomCode(roomCode);
          setCurrentView('websocket-multiplayer');
          
          // Don't show notification here - let WebSocketMultiplayer handle it
          // Clear the URL parameters to clean up the address bar
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } 
      // Handle /chess path with roomCode parameter
      else if (pathname === '/chess' || pathname.includes('chess')) {
        const roomCode = urlParams.get('roomCode');
        console.log('♟️ Chess path detected, roomCode:', roomCode);
        
        if (roomCode) {
          console.log('🔗 Deep link detected: joining chess room', roomCode);
          setDeepLinkRoomCode(roomCode);
          setCurrentView('chess');
          
          // Clear the URL parameters to clean up the address bar
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          // Just navigate to chess without room code
          setCurrentView('chess');
        }
      } 
      // Handle /checkers path with roomCode parameter
      else if (pathname === '/checkers' || pathname.includes('checkers')) {
        const roomCode = urlParams.get('roomCode');
        
        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('checkers');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('checkers');
        }
      }
      // Handle /connect-four path with roomCode parameter
      else if (pathname === '/connect-four' || pathname.includes('connect-four')) {
        const roomCode = urlParams.get('roomCode');
        
        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('connect-four');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('connect-four');
        }
      }
      // Handle /battleship path with roomCode parameter
      else if (pathname === '/battleship' || pathname.includes('battleship')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('battleship');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('battleship');
        }
      }
      // Handle /poker path with roomCode parameter
      else if (pathname === '/poker' || pathname.includes('poker')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('poker');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('poker');
        }
      }
      // Handle /mafia path with roomCode parameter
      else if (pathname === '/mafia' || pathname.includes('mafia')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('mafia');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('mafia');
        }
      }
      // Handle /snakes-and-ladders path with roomCode parameter
      else if (pathname === '/snakes-and-ladders' || pathname.includes('snakes-and-ladders')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('snakes-and-ladders');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('snakes-and-ladders');
        }
      }
      // Handle /ludo path with roomCode parameter
      else if (pathname === '/ludo' || pathname.includes('ludo')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('ludo');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('ludo');
        }
      }
      // Handle /teen-patti path with roomCode parameter
      else if (pathname === '/teen-patti' || pathname.includes('teen-patti')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('teen-patti');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('teen-patti');
        }
      }
      // Handle /reversi path with roomCode parameter
      else if (pathname === '/reversi' || pathname.includes('reversi')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('reversi');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('reversi');
        }
      }
      // Handle /mancala path with roomCode parameter
      else if (pathname === '/mancala' || pathname.includes('mancala')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('mancala');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('mancala');
        }
      }
      // Handle /backgammon path with roomCode parameter
      else if (pathname === '/backgammon' || pathname.includes('backgammon')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('backgammon');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('backgammon');
        }
      }
      // Handle /uno path with roomCode parameter
      else if (pathname === '/uno' || pathname.includes('uno')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('uno');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('uno');
        }
      }
      // Handle /pachisi path with roomCode parameter
      else if (pathname === '/pachisi' || pathname.includes('pachisi')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('pachisi');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('pachisi');
        }
      }
      // Handle /carrom path with roomCode parameter
      else if (pathname === '/carrom' || pathname.includes('carrom')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('carrom');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('carrom');
        }
      }
      // Handle /go path with roomCode parameter
      else if (pathname === '/go' || pathname.includes('go')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('go');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('go');
        }
      }
      // Handle /sudoku path
      else if (pathname === '/sudoku' || pathname.includes('sudoku')) {
        setCurrentView('sudoku');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle /2048 path
      else if (pathname === '/2048' || pathname.includes('2048')) {
        setCurrentView('2048');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle /minesweeper path
      else if (pathname === '/minesweeper' || pathname.includes('minesweeper')) {
        setCurrentView('minesweeper');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle /solitaire path
      else if (pathname === '/solitaire' || pathname.includes('solitaire')) {
        setCurrentView('solitaire');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle /hangman path
      else if (pathname === '/hangman' || pathname.includes('hangman')) {
        setCurrentView('hangman');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle /dots-and-boxes path with roomCode parameter
      else if (pathname === '/dots-and-boxes' || pathname.includes('dots-and-boxes')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('dots-and-boxes');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('dots-and-boxes');
        }
      }
      // Handle /rummy path with roomCode parameter
      else if (pathname === '/rummy' || pathname.includes('rummy')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('rummy');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('rummy');
        }
      }
      // Handle /pinball path
      else if (pathname === '/pinball' || pathname.includes('pinball')) {
        setCurrentView('pinball');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle /chaupar path with roomCode parameter
      else if (pathname === '/chaupar' || pathname.includes('chaupar')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('chaupar');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('chaupar');
        }
      }
      // Handle /golf-card-game path with roomCode parameter
      else if (pathname === '/golf-card-game' || pathname.includes('golf-card-game')) {
        const roomCode = urlParams.get('roomCode');

        if (roomCode) {
          setDeepLinkRoomCode(roomCode);
          setCurrentView('golf-card-game');
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setCurrentView('golf-card-game');
        }
      }
      // Handle /court-splitter path
      else if (pathname === '/court-splitter' || pathname.includes('court-splitter')) {
        setCurrentView('court-splitter');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle new game paths
      else if (pathname === '/wordle' || pathname.includes('wordle')) {
        setCurrentView('wordle');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      else if (pathname === '/word-game' || pathname.includes('word-game')) {
        setCurrentView('word-game');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      else if (pathname === '/yahtzee' || pathname.includes('yahtzee')) {
        setCurrentView('yahtzee');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      else if (pathname === '/billiards' || pathname.includes('billiards')) {
        setCurrentView('billiards');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      else if (pathname === '/trivia' || pathname.includes('trivia')) {
        setCurrentView('trivia');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      // Handle /admin path
      else if (pathname === '/admin' || pathname.startsWith('/admin')) {
        console.log('🛡️ Admin path detected');
        setCurrentView('admin');
      }
      else {
        console.log('🏠 Not a game path, staying on current view');
      }
    };

    // Handle deep link on initial load
    handleDeepLink();
    
    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleDeepLink);
    
    return () => {
      window.removeEventListener('popstate', handleDeepLink);
    };
  }, [addNotification]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const initializeApp = async () => {
    try {
      setIsLoading(true);
      
      // Load user preferences
      const preferences = await preferencesManager.getPreferences();
      if (preferences) {
        // Apply theme and other preferences
        applyPreferences(preferences);
      }
      
      // Check authentication status
      const savedProfile = await profileManager.getCurrentProfile();
      if (savedProfile) {
        // Auto-login with saved profile
        await login(savedProfile.authMethod, { token: 'saved' });
      }
      
    } catch (error) {
      errorActions.handleError(
        error instanceof Error ? error : new Error('Failed to initialize app'),
        ErrorType.SYSTEM,
        ErrorSeverity.HIGH
      );
    } finally {
      setIsLoading(false);
    }
  };

  const applyPreferences = (preferences: UserPreferences) => {
    // Apply theme
    document.documentElement.setAttribute('data-theme', preferences.theme);
    
    // Apply sound settings
    if (preferences.soundEnabled) {
      document.documentElement.setAttribute('data-sound', 'enabled');
    }
  };

  // Game flow handlers
  const startSinglePlayerGame = async (difficulty: AIDifficulty) => {
    try {
      setIsLoading(true);
      
      // Check guest game limit before starting a new game
      if (!isAuthenticated && guestGameCount >= 3) {
        addNotification({
          type: NotificationType.WARNING,
          title: 'Game Limit Reached',
          message: 'Guest game limit reached. Please create an account to continue playing.',
          duration: 5000
        });
        setCurrentView('settings');
        return;
      }
      
      const game = gameEngine.createGame(GameMode.SINGLE_PLAYER, difficulty);
      setCurrentGame(game);
      setGameState(gameEngine.getGameState(game.id));
      setCurrentView('game');
      
      // Start tracking the game session
      const gameSessionId = gameSessionManager.startGameSession('tic-tac-toe-single');
      setCurrentGameSessionId(gameSessionId);
      
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `Single player game started on ${difficulty} difficulty`,
        duration: 3000
      });
      
    } catch (error) {
      errorActions.handleError(
        error instanceof Error ? error : new Error('Failed to start single player game'),
        ErrorType.GAME_LOGIC,
        ErrorSeverity.MEDIUM
      );
    } finally {
      setIsLoading(false);
    }
  };

  const makeMove = async (position: Position) => {
    if (!currentGame || !gameState) return;
    
    try {
      // Get the current player ID from the game session
      const currentPlayerId = currentGame.currentPlayer;
      
      // For single player mode, ensure it's the human player's turn
      if (currentGame.mode === GameMode.SINGLE_PLAYER) {
        const humanPlayer = currentGame.players.find(p => p.type === PlayerType.HUMAN);
        if (!humanPlayer || currentPlayerId !== humanPlayer.id) {
          // It's AI's turn, don't allow human moves
          addNotification({
            type: NotificationType.WARNING,
            title: 'Not Your Turn',
            message: 'Please wait for the AI to make its move',
            duration: 2000
          });
          return;
        }
      }
      
      const result = gameEngine.makeMove(currentGame.id, position, currentPlayerId);
      
      if (result.success && result.gameState) {
        // Update both game session and game state
        const updatedGame = gameEngine.getGame(currentGame.id);
        if (updatedGame) {
          setCurrentGame(updatedGame);
        }
        setGameState(result.gameState);
        
        // Handle AI move in single player mode
        if (currentGame.mode === GameMode.SINGLE_PLAYER && result.gameState.status === GameStatus.IN_PROGRESS) {
          setTimeout(() => handleAIMove(), 500); // Small delay for better UX
        }
        
        // Handle game completion
        if (result.winResult && (result.winResult.hasWinner || result.winResult.isDraw)) {
          await handleGameComplete(result.winResult.winner, result.winResult.isDraw);
        }
      } else {
        addNotification({
          type: NotificationType.ERROR,
          title: 'Invalid Move',
          message: result.error || 'Invalid move',
          duration: 2000
        });
      }
    } catch (error) {
      errorActions.handleError(
        error instanceof Error ? error : new Error('Failed to make move'),
        ErrorType.GAME_LOGIC,
        ErrorSeverity.LOW
      );
    }
  };

  const handleAIMove = async () => {
    if (!currentGame || !gameState) return;
    
    try {
      // Read the latest game state from the engine (avoids stale closure issue)
      const latestGame = gameEngine.getGame(currentGame.id);
      if (!latestGame) return;

      const aiPlayer = latestGame.players.find(p => p.type === PlayerType.AI);
      if (!aiPlayer || latestGame.currentPlayer !== aiPlayer.id) return;
      
      // Import AIPlayer dynamically to avoid circular dependencies
      const { AIPlayer } = await import('../services/AIPlayer');
      const aiPlayerService = new AIPlayer();
      
      // Calculate AI move using latest board from engine
      const latestState = gameEngine.getGameState(currentGame.id);
      if (!latestState) return;
      const aiMove = aiPlayerService.calculateMove(latestState.board, aiPlayer.difficulty!);
      
      // Make AI move using the same gameEngine instance
      const result = gameEngine.makeMove(currentGame.id, aiMove, aiPlayer.id);
      
      if (result.success && result.gameState) {
        // Update both game session and game state
        const updatedGame = gameEngine.getGame(currentGame.id);
        if (updatedGame) {
          setCurrentGame(updatedGame);
        }
        setGameState(result.gameState);
        
        // Handle game completion
        if (result.winResult && (result.winResult.hasWinner || result.winResult.isDraw)) {
          await handleGameComplete(result.winResult.winner, result.winResult.isDraw);
        }
      }
    } catch (error) {
      errorActions.handleError(
        error instanceof Error ? error : new Error('AI move failed'),
        ErrorType.GAME_LOGIC,
        ErrorSeverity.MEDIUM
      );
    }
  };

  const handleGameComplete = async (winner?: string, isDraw?: boolean) => {
    if (!currentGame) return;
    
    try {
      // Record game result
      const gameResult: GameResult = {
        gameId: currentGame.id,
        winner,
        isDraw: isDraw || false,
        mode: currentGame.mode,
        difficulty: currentGame.players.find(p => p.type === PlayerType.AI)?.difficulty,
        duration: Date.now() - currentGame.createdAt.getTime(),
        completedAt: new Date()
      };
      
      await statsManager.recordGameResult(gameResult);
      
      // End the game session tracking
      if (currentGameSessionId) {
        gameSessionManager.endGameSession(currentGameSessionId, true);
      }
      
      // Increment guest game count if not authenticated (only when game completes)
      if (!isAuthenticated && currentGame.mode === GameMode.SINGLE_PLAYER) {
        incrementGuestGameCount();
      }
      
      // Show completion message
      let message = '';
      if (isDraw) {
        message = "It's a draw!";
      } else {
        const humanPlayer = currentGame.players.find(p => p.type === PlayerType.HUMAN);
        if (winner === humanPlayer?.id) {
          message = 'Congratulations! You won!';
        } else {
          message = 'Game over. Better luck next time!';
        }
      }
      
      addNotification({
        type: isDraw ? NotificationType.INFO : (winner === currentGame.players.find(p => p.type === PlayerType.HUMAN)?.id ? NotificationType.SUCCESS : NotificationType.WARNING),
        title: 'Game Complete',
        message,
        duration: 5000
      });
      
    } catch (error) {
      errorActions.handleError(
        error instanceof Error ? error : new Error('Failed to record game result'),
        ErrorType.SYSTEM,
        ErrorSeverity.LOW
      );
    }
  };

  const returnToMenu = () => {
    // End current game session if it exists
    if (currentGameSessionId) {
      gameSessionManager.endGameSession(currentGameSessionId, false); // Mark as incomplete
      setCurrentGameSessionId(null);
    }
    
    setCurrentGame(null);
    setGameState(null);
    setCurrentView('menu');
    setDeepLinkRoomCode(null); // Clear deep link room code when returning to menu
    window.history.pushState({}, '', '/');
  };

  const navigateToHub = () => {
    setCurrentView('hub');
    setDeepLinkRoomCode(null);
    window.history.pushState({}, '', '/');
  };

  // Auth menu handlers
  const handleAuthButtonClick = () => {
    setShowAuthMenu(!showAuthMenu);
  };

  const handleAuthMenuClose = () => {
    setShowAuthMenu(false);
  };

  const handleAuthMethodSelect = async (method: AuthMethod) => {
    setShowAuthMenu(false);
    
    try {
      const result = await login(method);
      
      if (result.success) {
        addNotification({
          type: NotificationType.SUCCESS,
          title: 'Welcome!',
          message: `Successfully logged in with ${method}`,
          duration: 3000
        });
      } else {
        addNotification({
          type: NotificationType.ERROR,
          title: 'Login Failed',
          message: result.error || 'Unable to log in. Please try again.',
          duration: 5000
        });
      }
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Login Error',
        message: 'An unexpected error occurred. Please try again.',
        duration: 5000
      });
    }
  };

  const handleAuthLogout = async () => {
    // TODO: Show logout confirmation dialog
    try {
      await logout();
      setShowAuthMenu(false);
      
      addNotification({
        type: NotificationType.INFO,
        title: 'Logged Out',
        message: 'You have been successfully logged out.',
        duration: 3000
      });
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Logout Error',
        message: 'Unable to log out. Please try again.',
        duration: 5000
      });
    }
  };

  const handleViewStatistics = () => {
    setShowAuthMenu(false);
    setCurrentView('stats');
  };

  const handleViewSettings = () => {
    setShowAuthMenu(false);
    setCurrentView('settings');
  };

  const handleViewPreferences = () => {
    setShowAuthMenu(false);
    setCurrentView('settings');
  };

  const handleViewAbout = () => {
    setShowAuthMenu(false);
    addNotification({
      type: NotificationType.INFO,
      title: 'PlayHub Games AI',
      message: 'Version 1.0.0 - Multi-game platform with AI opponents',
      duration: 5000
    });
  };

  // Email authentication handlers
  const handleEmailSignup = async (email: string, password: string, name?: string) => {
    try {
      const result = await createProfile(AuthMethod.CUSTOM, { email, password });
      
      setShowAuthMenu(false);
      
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Account Created!',
        message: `Welcome ${name || email}! Your account has been created successfully.`,
        duration: 5000
      });
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Signup Failed',
        message: error instanceof Error ? error.message : 'Unable to create account. Please try again.',
        duration: 5000
      });
      throw error;
    }
  };

  const handleEmailLogin = async (email: string, password: string, rememberMe: boolean) => {
    try {
      const result = await login(AuthMethod.CUSTOM, { email, password });
      
      if (result.success) {
        setShowAuthMenu(false);
        
        addNotification({
          type: NotificationType.SUCCESS,
          title: 'Welcome Back!',
          message: `Successfully logged in as ${email}`,
          duration: 3000
        });
      } else {
        addNotification({
          type: NotificationType.ERROR,
          title: 'Login Failed',
          message: result.error || 'Invalid email or password.',
          duration: 5000
        });
        throw new Error(result.error || 'Login failed');
      }
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Login Error',
        message: error instanceof Error ? error.message : 'An unexpected error occurred.',
        duration: 5000
      });
      throw error;
    }
  };

  const handlePasswordReset = async (email: string) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Email Sent',
        message: `Password reset link sent to ${email}`,
        duration: 5000
      });
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Reset Failed',
        message: 'Unable to send reset email. Please try again.',
        duration: 5000
      });
      throw error;
    }
  };

  const handlePreferencesChange = async (preferences: UserPreferences) => {
    try {
      await preferencesManager.savePreferences(preferences);
      applyPreferences(preferences);
      
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Settings Saved',
        message: 'Preferences saved successfully',
        duration: 2000
      });
    } catch (error) {
      errorActions.handleError(
        error instanceof Error ? error : new Error('Failed to save preferences'),
        ErrorType.SYSTEM,
        ErrorSeverity.LOW
      );
    }
  };

  // Render different views
  const renderCurrentView = () => {
    if (isLoading) {
      return (
        <ResponsiveLayout maxWidth="sm" centered>
          <LoadingIndicator message="Loading..." />
        </ResponsiveLayout>
      );
    }

    switch (currentView) {
      case 'hub':
        return (
          <GameHub onGameSelect={(gameId) => {
            // Update browser URL to reflect the game being played
            const urlPath = gameId === 'tic-tac-toe' ? '/tic-tac-toe' : `/${gameId}`;
            window.history.pushState({ game: gameId }, '', urlPath);

            if (gameId === 'tic-tac-toe') {
              setCurrentView('menu');
            } else if (gameId === 'chess') {
              setCurrentView('chess');
            } else if (gameId === 'checkers') {
              setCurrentView('checkers');
            } else if (gameId === 'connect-four') {
              setCurrentView('connect-four');
            } else if (gameId === 'battleship') {
              setCurrentView('battleship');
            } else if (gameId === 'poker') {
              setCurrentView('poker');
            } else if (gameId === 'mafia') {
              setCurrentView('mafia');
            } else if (gameId === 'snakes-and-ladders') {
              setCurrentView('snakes-and-ladders');
            } else if (gameId === 'ludo') {
              setCurrentView('ludo');
            } else if (gameId === 'teen-patti') {
              setCurrentView('teen-patti');
            } else if (gameId === 'reversi') {
              setCurrentView('reversi');
            } else if (gameId === 'mancala') {
              setCurrentView('mancala');
            } else if (gameId === 'backgammon') {
              setCurrentView('backgammon');
            } else if (gameId === 'uno') {
              setCurrentView('uno');
            } else if (gameId === 'pachisi') {
              setCurrentView('pachisi');
            } else if (gameId === 'carrom') {
              setCurrentView('carrom');
            } else if (gameId === 'go') {
              setCurrentView('go');
            } else if (gameId === 'sudoku') {
              setCurrentView('sudoku');
            } else if (gameId === '2048') {
              setCurrentView('2048');
            } else if (gameId === 'minesweeper') {
              setCurrentView('minesweeper');
            } else if (gameId === 'solitaire') {
              setCurrentView('solitaire');
            } else if (gameId === 'hangman') {
              setCurrentView('hangman');
            } else if (gameId === 'dots-and-boxes') {
              setCurrentView('dots-and-boxes');
            } else if (gameId === 'rummy') {
              setCurrentView('rummy');
            } else if (gameId === 'pinball') {
              setCurrentView('pinball');
            } else if (gameId === 'chaupar') {
              setCurrentView('chaupar');
            } else if (gameId === 'golf-card-game') {
              setCurrentView('golf-card-game');
            } else if (gameId === 'court-splitter') {
              setCurrentView('court-splitter');
            } else if (gameId === 'wordle') {
              setCurrentView('wordle');
            } else if (gameId === 'word-game') {
              setCurrentView('word-game');
            } else if (gameId === 'yahtzee') {
              setCurrentView('yahtzee');
            } else if (gameId === 'billiards') {
              setCurrentView('billiards');
            } else if (gameId === 'trivia') {
              setCurrentView('trivia');
            }
          }} />
        );

      case 'menu':
        return (
          <ResponsiveLayout maxWidth="sm" centered>
            <ResponsiveStack direction="column" gap="lg" align="center">
              {/* Game Mode Selection */}
              <UniversalGameModeSelector
                config={{
                  gameName: 'Tic-Tac-Toe',
                  gameIcon: '⭕',
                  availableModes: ['ai', 'online'],
                }}
                onStart={(selection: GameModeSelection) => {
                  if (selection.mode === 'ai' && selection.difficulty) {
                    startSinglePlayerGame(selection.difficulty as AIDifficulty);
                  }
                }}
                onCreateRoom={() => setCurrentView('websocket-multiplayer')}
                onJoinRoom={() => setCurrentView('websocket-multiplayer')}
                guestGamesRemaining={Math.max(0, 3 - guestGameCount)}
                isAuthenticated={isAuthenticated}
                isLoading={isLoading}
              />
            </ResponsiveStack>
          </ResponsiveLayout>
        );

      case 'game': {
        if (!currentGame || !gameState) {
          return (
            <ResponsiveLayout maxWidth="sm" centered>
              <div>Loading game...</div>
            </ResponsiveLayout>
          );
        }

        const humanPlayer = currentGame.players.find(p => p.type === PlayerType.HUMAN);
        const aiPlayer = currentGame.players.find(p => p.type === PlayerType.AI);
        const isHumanTurn = currentGame.currentPlayer === humanPlayer?.id;
        const isGameOver = gameState.status === GameStatus.COMPLETED;
        const isHumanWinner = gameState.winner === humanPlayer?.id;
        const isDraw = isGameOver && !gameState.winner;

        const tttConfig: UniversalGameLayoutConfig = {
          gameName: 'Tic-Tac-Toe',
          fullWidth: true,

          board: (
            <GameBoard
              gameState={gameState}
              onCellClick={makeMove}
              disabled={!isHumanTurn || isGameOver}
            />
          ),

          statusBar: {
            turnIndicator: (
              <span>
                {isGameOver
                  ? (isDraw ? '🤝 Draw' : isHumanWinner ? '🎉 You Win!' : '💔 AI Wins')
                  : isHumanTurn
                    ? '🎯 Your Turn (X)'
                    : '⏳ AI Thinking... (O)'}
              </span>
            ),
            gameStatus: isGameOver
              ? 'Completed'
              : 'In Progress',
            gameStatusType: isGameOver ? 'ended' : 'active',
            aiThinking: !isGameOver && !isHumanTurn,
          },

          actions: [
            {
              id: 'new-game',
              label: 'New Game',
              icon: '🔄',
              onClick: () => {
                const difficulty = aiPlayer?.difficulty;
                if (difficulty) startSinglePlayerGame(difficulty);
              },
              variant: 'primary',
              disabled: !isGameOver,
            },
            {
              id: 'back-to-menu',
              label: 'Menu',
              icon: '🏠',
              onClick: returnToMenu,
              variant: 'secondary',
            },
          ],

          gameOver: isGameOver
            ? {
                show: true,
                result: isDraw ? 'draw' : isHumanWinner ? 'win' : 'loss',
                title: isDraw
                  ? "🤝 IT'S A DRAW!"
                  : isHumanWinner
                    ? '🎉 YOU WIN!'
                    : '💔 AI WINS',
                subtitle: isDraw
                  ? 'Great game! No winner this time.'
                  : isHumanWinner
                    ? 'Congratulations! You beat the AI!'
                    : 'Better luck next time!',
                onPlayAgain: () => {
                  const difficulty = aiPlayer?.difficulty;
                  if (difficulty) startSinglePlayerGame(difficulty);
                },
                onExit: returnToMenu,
              }
            : undefined,

          howToPlayContent: allGameInstructions['tic-tac-toe'] ? (
            <HowToPlay instructions={allGameInstructions['tic-tac-toe']} />
          ) : undefined,
        };

        return <UniversalGameLayout config={tttConfig} />;
      }

      case 'stats':
        return (
          <ResponsiveLayout maxWidth="lg">
            <ResponsiveStack direction="column" gap="lg">
              <div className="view-header">
                <h2>Statistics</h2>
                <ResponsiveButton
                  variant="outline"
                  size="small"
                  onClick={returnToMenu}
                >
                  Back to Menu
                </ResponsiveButton>
              </div>
              <StatisticsDisplay 
                statisticsManager={statsManager}
                profileManager={profileManager}
              />
            </ResponsiveStack>
          </ResponsiveLayout>
        );

      case 'settings':
        return (
          <ResponsiveLayout maxWidth="md">
            <ResponsiveStack direction="column" gap="lg">
              <div className="view-header">
                <h2>Settings</h2>
                <ResponsiveButton
                  variant="outline"
                  size="small"
                  onClick={returnToMenu}
                >
                  Back to Menu
                </ResponsiveButton>
              </div>
              <CustomizationPanel
                profile={profile}
                onPreferencesChange={handlePreferencesChange}
                onLogin={login}
                onLogout={logout}
                isAuthenticated={isAuthenticated}
                guestGamesRemaining={Math.max(0, 3 - guestGameCount)}
              />
            </ResponsiveStack>
          </ResponsiveLayout>
        );

      case 'websocket-multiplayer':
        return (
          <WebSocketMultiplayer
            onBackToMenu={returnToMenu}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'chess':
        return (
          <ChessGame
            key="chess-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'checkers':
        return (
          <CheckersGame
            key="checkers-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'connect-four':
        return (
          <ConnectFourGame
            key="connect-four-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'battleship':
        return (
          <BattleshipGame
            key="battleship-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'poker':
        return (
          <PokerGame
            key="poker-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'mafia':
        return (
          <MafiaGame
            key="mafia-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'snakes-and-ladders':
        return (
          <SnakesLaddersGame
            key="snakes-and-ladders-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'ludo':
        return (
          <LudoGame
            key="ludo-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'teen-patti':
        return (
          <TeenPattiGame
            key="teen-patti-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'reversi':
        return (
          <ReversiGame
            key="reversi-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'mancala':
        return (
          <MancalaGame
            key="mancala-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'backgammon':
        return (
          <BackgammonGame
            key="backgammon-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'uno':
        return (
          <UnoGame
            key="uno-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'pachisi':
        return (
          <PachisiGame
            key="pachisi-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'carrom':
        return (
          <CarromGame
            key="carrom-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'go':
        return (
          <GoGame
            key="go-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'sudoku':
        return (
          <SudokuGame
            key="sudoku-game"
            onExit={navigateToHub}
          />
        );

      case '2048':
        return (
          <Game2048
            key="2048-game"
            onExit={navigateToHub}
          />
        );

      case 'minesweeper':
        return (
          <MinesweeperGame
            key="minesweeper-game"
            onExit={navigateToHub}
          />
        );

      case 'solitaire':
        return (
          <SolitaireGame
            key="solitaire-game"
            onExit={navigateToHub}
          />
        );

      case 'hangman':
        return (
          <HangmanGame
            key="hangman-game"
            onExit={navigateToHub}
          />
        );

      case 'dots-and-boxes':
        return (
          <DotsBoxesGame
            key="dots-and-boxes-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'rummy':
        return (
          <RummyGame
            key="rummy-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'pinball':
        return (
          <PinballGame
            key="pinball-game"
            onBack={navigateToHub}
          />
        );

      case 'chaupar':
        return (
          <ChauparGame
            key="chaupar-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'golf-card-game':
        return (
          <GolfGame
            key="golf-card-game"
            onExit={navigateToHub}
            initialRoomCode={deepLinkRoomCode || undefined}
          />
        );

      case 'court-splitter':
        return (
          <CourtSplitter
            key="court-splitter"
            onExit={navigateToHub}
          />
        );

      case 'wordle':
        return (
          <WordleGame
            key="wordle"
            onExit={navigateToHub}
          />
        );

      case 'word-game':
        return (
          <WordGame
            key="word-game"
            onExit={navigateToHub}
          />
        );

      case 'yahtzee':
        return (
          <YahtzeeGame
            key="yahtzee"
            onExit={navigateToHub}
          />
        );

      case 'billiards':
        return (
          <BilliardsGame
            key="billiards"
            onExit={navigateToHub}
          />
        );

      case 'trivia':
        return (
          <TriviaGame
            key="trivia"
            onExit={navigateToHub}
          />
        );

      default:
        return null;
    }
  };

  // Map view to display name for the navbar
  const getGameDisplayName = (): string | null => {
    const nameMap: Record<string, string> = {
      chess: 'Chess', checkers: 'Checkers', 'connect-four': 'Connect Four',
      battleship: 'Battleship', poker: 'Poker', mafia: 'Mafia',
      'snakes-and-ladders': 'Snakes & Ladders', ludo: 'Ludo',
      'teen-patti': 'Teen Patti', reversi: 'Reversi', mancala: 'Mancala',
      backgammon: 'Backgammon', uno: 'UNO', pachisi: 'Pachisi', carrom: 'Carrom',
      go: 'Go',
      sudoku: 'Sudoku',
      '2048': '2048',
      minesweeper: 'Minesweeper',
      pinball: 'Pinball',
      chaupar: 'Chaupar',
      'golf-card-game': 'Golf Card Game',
      'court-splitter': 'Court Splitter',
      wordle: 'Daily Word',
      'word-game': 'Word Clash',
      yahtzee: 'Yahtzee',
      billiards: '8-Ball Pool',
      trivia: 'Trivia',
      solitaire: 'Solitaire',
      hangman: 'Hangman',
      'dots-and-boxes': 'Dots & Boxes',
      rummy: 'Rummy',
      menu: 'Tic-Tac-Toe', game: 'Tic-Tac-Toe', 'websocket-multiplayer': 'Tic-Tac-Toe',
      stats: 'Statistics', settings: 'Settings',
    };
    return nameMap[currentView] || null;
  };

  // Build game-specific menu items based on current view
  const getGameMenuItems = (): NavMenuItem[] => {
    const isGameView = !['hub', 'stats', 'settings'].includes(currentView);
    if (!isGameView) return [];

    const items: NavMenuItem[] = [];

    // Common items for all games
    items.push({
      id: 'new-game',
      label: 'New Game',
      icon: '🔄',
      onClick: () => setCurrentView(currentView), // Re-mount the game
    });

    // Tic-Tac-Toe specific
    if (currentView === 'menu' || currentView === 'game' || currentView === 'websocket-multiplayer') {
      if (currentView === 'game') {
        items.push({
          id: 'back-to-menu',
          label: 'Change Mode',
          icon: '🎮',
          onClick: () => setCurrentView('menu'),
        });
      }
    }

    // Stats and settings for all game views
    items.push({
      id: 'divider-1',
      label: '',
      divider: true,
      onClick: () => {},
    });

    items.push({
      id: 'view-stats',
      label: 'Statistics',
      icon: '📊',
      onClick: () => setCurrentView('stats'),
    });

    items.push({
      id: 'view-settings',
      label: 'Settings',
      icon: '⚙️',
      onClick: () => setCurrentView('settings'),
    });

    items.push({
      id: 'divider-2',
      label: '',
      divider: true,
      onClick: () => {},
    });

    items.push({
      id: 'exit-to-hub',
      label: 'Exit to Game Hub',
      icon: '🏠',
      onClick: navigateToHub,
      danger: false,
    });

    return items;
  };

  // If on admin route, render AdminApp directly (no TopNavBar)
  if (currentView === 'admin') {
    return (
      <ErrorBoundary componentName="AdminApp">
        <AdminApp />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary componentName="GameApp">
      <div className="game-app">
        {/* Global Navigation Bar */}
        <TopNavBar
          currentGame={getGameDisplayName()}
          onHome={navigateToHub}
          isAuthenticated={isAuthenticated}
          user={profile}
          onAuthClick={handleAuthButtonClick}
          isHub={currentView === 'hub'}
          menuItems={getGameMenuItems()}
        />
        
        {renderCurrentView()}

        {/* How to Play — foldable instructions at the bottom of every game */}
        {currentView !== 'hub' && currentView !== 'stats' && currentView !== 'settings' && (
          allGameInstructions[currentView] ? (
            <HowToPlay instructions={allGameInstructions[currentView]} />
          ) : (currentView === 'menu' || currentView === 'game' || currentView === 'websocket-multiplayer') ? (
            <HowToPlay instructions={allGameInstructions['tic-tac-toe']} />
          ) : null
        )}
        
        {/* Auth Menu */}
        <AuthMenu
          isOpen={showAuthMenu}
          isAuthenticated={isAuthenticated}
          user={profile}
          onClose={handleAuthMenuClose}
          onAuthMethodSelect={handleAuthMethodSelect}
          onEmailSignup={handleEmailSignup}
          onEmailLogin={handleEmailLogin}
          onPasswordReset={handlePasswordReset}
          onLogout={handleAuthLogout}
          onViewStatistics={handleViewStatistics}
          onViewSettings={handleViewSettings}
          onViewPreferences={handleViewPreferences}
          onViewAbout={handleViewAbout}
          isMobile={isMobile}
          isLoading={authLoading}
        />
      </div>
    </ErrorBoundary>
  );
};

export default GameApp;